module.exports = {
    slug: 'mentions-obligatoires-facture-2026',
    title: 'Mentions obligatoires sur une facture en 2026 : la liste',
    h1: 'Mentions obligatoires sur une facture en 2026 : la liste complète',
    description: 'Toutes les mentions d\'une facture entre professionnels, les 4 nouvelles mentions de la facturation électronique, les cas particuliers et les sanctions.',
    category: 'Facturation',
    published: '2026-10-19',
    updated: '2026-10-19',
    lead: 'Une facture oubliant une mention obligatoire peut être sanctionnée, et elle fait perdre du temps au client qui doit la faire corriger. Depuis le 1er septembre 2026, **quatre mentions** s\'ajoutent à la liste habituelle pour les entreprises établies en France et assujetties à la TVA. Voici la liste à jour.',
    sections: [
        {
            id: 'generales',
            h2: 'Les mentions générales',
            blocks: [
                {table: {
                    head: ['Mention', 'Précisions'],
                    rows: [
                        ['Date d\'émission', 'Date à laquelle la facture est établie'],
                        ['Numéro unique', 'Basé sur une séquence chronologique et continue, sans trou ni doublon'],
                        ['Date de la vente ou de la prestation', 'Ou date de versement de l\'acompte, si elle est différente de celle de la facture'],
                        ['Identité du vendeur', 'Nom complet, adresse, numéro d\'identification de l\'entreprise'],
                        ['Identité de l\'acheteur', 'Nom complet et adresse ; adresse de facturation si elle diffère'],
                        ['Numéro de bon de commande', 'Si l\'acheteur en a établi un'],
                        ['Numéros de TVA', 'Celui du vendeur et celui du client professionnel redevable de la TVA ; non obligatoires pour une facture de 150 € HT ou moins'],
                        ['Détail de chaque bien ou service', 'Quantité, dénomination précise, prix unitaire HT, taux de TVA ou exonération']
                    ]
                }}
            ]
        },
        {
            id: 'prix-paiement',
            h2: 'Prix, remises et paiement',
            blocks: [
                {ul: [
                    '**Totaux** hors taxes et toutes taxes comprises.',
                    '**Réductions de prix** (rabais, remises, ristournes, escomptes) lorsqu\'elles sont chiffrables et liées à l\'opération.',
                    '**Date ou délai de paiement** et conditions d\'escompte.',
                    '**Taux des pénalités de retard** applicable en cas de paiement tardif.',
                    '**Indemnité forfaitaire de 40 €** pour frais de recouvrement.',
                    'Pour une facture adressée à un **particulier** : l\'existence et la durée de la garantie légale de conformité (au moins deux ans) pour certaines catégories de biens, hors ventes à distance ou hors établissement.'
                ]},
                {p: 'Le détail des pénalités est dans [facture impayée : relance et pénalités](/blog/facture-impayee-relance-penalites-retard).'}
            ]
        },
        {
            id: 'nouvelles',
            h2: 'Les quatre nouvelles mentions de la facturation électronique',
            blocks: [
                {p: 'À partir du **1er septembre 2026**, selon le calendrier progressif de la réforme, les entreprises établies en France et assujetties à la TVA ajoutent :'},
                {ol: [
                    'le **numéro SIREN du client** ;',
                    'l\'**adresse de livraison des biens**, si elle diffère de l\'adresse de facturation ;',
                    'l\'indication que les opérations sont **exclusivement des livraisons de biens, des prestations de services, ou les deux** ;',
                    'l\'**option pour le paiement de la TVA d\'après les débits**, le cas échéant.'
                ]},
                {p: 'Ces informations doivent figurer dans des champs dédiés de la facture électronique. Pour le calendrier complet, voir [facturation électronique 2026-2027](/blog/facturation-electronique-2026-2027-calendrier-obligations).'}
            ]
        },
        {
            id: 'particulieres',
            h2: 'Les mentions particulières selon votre situation',
            blocks: [
                {table: {
                    head: ['Situation', 'Mention à ajouter'],
                    rows: [
                        ['Franchise en base de TVA', '« TVA non applicable, art. 293 B du CGI »'],
                        ['Autoliquidation (l\'acquéreur est redevable de la TVA)', '« Auto-liquidation »'],
                        ['Membre d\'une association ou d\'un centre de gestion agréé', 'Mention sur l\'acceptation du chèque et de la carte bancaire']
                    ]
                }},
                {note: 'Pour la franchise en base, service-public.fr indique qu\'à compter du 1er septembre 2026 la référence à l\'article 293 B du CGI évolue vers le code des impositions sur les biens et services (CIBS), avec une tolérance jusqu\'au 31 décembre 2027. Vérifiez la formulation en vigueur auprès de votre expert-comptable.'}
            ]
        },
        {
            id: 'sanctions',
            h2: 'Sanctions et conservation',
            blocks: [
                {ul: [
                    'Une mention **manquante ou inexacte** est sanctionnée de 15 € par mention, dans la limite de 25 % du montant de la facture (service-public.fr).',
                    'Les factures émises ou reçues se **conservent dix ans**.',
                    'Une facture ne se modifie pas après émission : on la corrige par un [avoir](/blog/facture-avoir-comment-etablir) ou une facture rectificative.'
                ]}
            ]
        },
        {
            id: 'logiciel',
            h2: 'Comment ne rien oublier ?',
            blocks: [
                {p: 'Un logiciel de facturation prérempli vos coordonnées, numérote les documents dans l\'ordre et calcule les totaux. Vérifiez qu\'il gère l\'**acompte**, l\'**avoir** et les **nouvelles mentions**. Voir [comment choisir son logiciel de facturation](/blog/choisir-logiciel-facturation-tpe-independant).'}
            ]
        }
    ],
    faq: [
        {q: 'Le numéro de TVA est-il obligatoire sur toutes les factures ?', a: 'Non. Les numéros de TVA du vendeur et du client professionnel ne sont pas obligatoires pour une facture de 150 € HT ou moins.'},
        {q: 'Quelle mention pour un micro-entrepreneur en franchise de TVA ?', a: '« TVA non applicable, art. 293 B du CGI ». À compter du 1er septembre 2026, la référence évolue vers le CIBS, avec une tolérance jusqu\'au 31 décembre 2027 selon service-public.fr.'},
        {q: 'Combien de temps conserver une facture ?', a: 'Dix ans, qu\'elle soit émise ou reçue.'},
        {q: 'Le numéro SIREN du client est-il obligatoire ?', a: 'Oui, à partir du 1er septembre 2026 pour les entreprises établies en France et assujetties à la TVA, selon le calendrier progressif de la réforme.'}
    ],
    sources: [
        {label: 'economie.gouv.fr : mentions obligatoires d\'une facture', url: 'https://www.economie.gouv.fr/entreprises/gerer-son-entreprise-au-quotidien/gerer-sa-comptabilite-et-ses-demarches/mentions-obligatoires-dune-facture-tout-savoir'},
        {label: 'service-public.fr : mentions obligatoires sur une facture', url: 'https://entreprendre.service-public.gouv.fr/vosdroits/F31808'},
        {label: 'service-public.fr : tout savoir sur la facturation', url: 'https://entreprendre.service-public.gouv.fr/vosdroits/F23208'},
        {label: 'Légifrance : article L441-9 du code de commerce (mentions de la facture)', url: 'https://www.legifrance.gouv.fr/codes/article_lc/LEGIARTI000038414397/'},
        {label: 'service-public.fr : facturation électronique, le calendrier de la réforme', url: 'https://entreprendre.service-public.gouv.fr/actualites/A15683'},
        {label: 'Légifrance : article L123-22 du code de commerce (conservation des documents comptables)', url: 'https://www.legifrance.gouv.fr/codes/article_lc/LEGIARTI000006219327/'}
    ],
    related: ['facturation-electronique-2026-2027-calendrier-obligations', 'facture-impayee-relance-penalites-retard', 'facture-avoir-comment-etablir']
};
